import { Authenticated, Refine } from "@refinedev/core";
import {
  AuthPage,
  ErrorComponent,
  RefineThemes,
  ThemedLayout,
  ThemedTitle,
  useNotificationProvider,
} from "@refinedev/antd";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import dataProvider from "@refinedev/simple-rest";
import { App as AntdApp, ConfigProvider } from "antd";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import "@refinedev/antd/dist/reset.css";
import { AudioOutlined, StopOutlined, UserOutlined } from "@ant-design/icons";
import { UserList, UserCreatePage, UserEdit, UserShow } from "pages/users";
import { PodcastList, PodcastShow, PodcastEdit } from "pages/podcasts";
import { BlockedUrlCreatePage, BlockedUrlEdit, BlockedUrlList } from "pages/blockedUrls";
import { authProvider, axiosInstance } from "./authProvider";

// Resources point at routes (refine 4+); the pages themselves are mounted below.
const resources = [
  {
    name: "blocked-urls",
    list: "/blocked-urls",
    create: "/blocked-urls/create",
    edit: "/blocked-urls/edit/:id",
    meta: { label: "Blocked URLs", icon: <StopOutlined /> },
  },
  {
    name: "podcasts",
    list: "/podcasts",
    edit: "/podcasts/edit/:id",
    show: "/podcasts/show/:id",
    meta: { icon: <AudioOutlined /> },
  },
  {
    name: "users",
    list: "/users",
    create: "/users/create",
    edit: "/users/edit/:id",
    show: "/users/show/:id",
    meta: { icon: <UserOutlined /> },
  },
];

function App() {
  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ConfigProvider theme={RefineThemes.Blue}>
          <AntdApp>
            <Refine
              routerProvider={routerProvider}
              dataProvider={dataProvider("/api/v1", axiosInstance)}
              authProvider={authProvider}
              notificationProvider={useNotificationProvider}
              resources={resources}
              options={{ warnWhenUnsavedChanges: true }}
            >
              <Routes>
                <Route
                  element={
                    <Authenticated key="authenticated-routes" fallback={<CatchAllNavigate to="/login" />}>
                      <ThemedLayout Title={({ collapsed }) => <ThemedTitle collapsed={collapsed} text="PC.ST admin" />}>
                        <Outlet />
                      </ThemedLayout>
                    </Authenticated>
                  }
                >
                  <Route index element={<NavigateToResource resource="blocked-urls" />} />
                  <Route path="/blocked-urls">
                    <Route index element={<BlockedUrlList />} />
                    <Route path="create" element={<BlockedUrlCreatePage />} />
                    <Route path="edit/:id" element={<BlockedUrlEdit />} />
                  </Route>
                  <Route path="/podcasts">
                    <Route index element={<PodcastList />} />
                    <Route path="edit/:id" element={<PodcastEdit />} />
                    <Route path="show/:id" element={<PodcastShow />} />
                  </Route>
                  <Route path="/users">
                    <Route index element={<UserList />} />
                    <Route path="create" element={<UserCreatePage />} />
                    <Route path="edit/:id" element={<UserEdit />} />
                    <Route path="show/:id" element={<UserShow />} />
                  </Route>
                  <Route path="*" element={<ErrorComponent />} />
                </Route>
                <Route
                  element={
                    <Authenticated key="auth-pages" fallback={<Outlet />}>
                      <NavigateToResource resource="blocked-urls" />
                    </Authenticated>
                  }
                >
                  <Route
                    path="/login"
                    element={<AuthPage type="login" registerLink={false} forgotPasswordLink={false} rememberMe={false} />}
                  />
                </Route>
              </Routes>
              <RefineKbar />
              <UnsavedChangesNotifier />
              <DocumentTitleHandler />
            </Refine>
          </AntdApp>
        </ConfigProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
